import React, { useRef, useEffect } from "react";
import { useFormContext, useController } from "react-hook-form";
import "./Form.css";
import { TextCss } from "../../components/GlobalCss/TextCss";

interface EmailInputProps {
  name: string;
  label?: string;
  required?: boolean;
  placeholder?: string;
  requiredMessage?: string;
  disabled?: boolean;
  validation?: {
    required?: string | boolean;
    pattern?: { value: RegExp; message: string };
    validate?: (value: any) => boolean | string;
    [key: string]: any;
  };
  className?: string;
}

const EmailInput: React.FC<EmailInputProps> = ({
  label = "Email",
  name,
  required = false,
  placeholder = "Enter your email...",
  requiredMessage = "This field is required",
  disabled = false,
  validation = {},
  className = "",
  ...rest
}) => {
  const {
    control,
    formState: { isSubmitted },
    setError,
    clearErrors,
  } = useFormContext();
  const timeoutRef = useRef<NodeJS.Timeout|null>(null);

  const {
    field: { onChange, value, ...fieldRest },
    fieldState: { isTouched, error },
  } = useController({
    name,
    control,
    rules: {
      required: required ? requiredMessage : false,
      maxLength: { value: 50, message: "Email cannot exceed 50 characters" },
      pattern: {
        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
        message: "Please enter a valid email address",
        ...validation.pattern,
      },
      ...validation,
    },
  });

  const showErrorMessage = (isSubmitted || error?.type === 'maxLength') ? error?.message : undefined;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.toLowerCase();
    
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    const maxLengthRule = validation?.maxLength;
    const maxLength = maxLengthRule?.value || maxLengthRule || 50;
    
    if (val.length > maxLength) {
      // Manual trigger error
      const message = maxLengthRule?.message || `Maximum length of ${maxLength} characters reached`;
      setError(name, { type: 'maxLength', message });

      timeoutRef.current = setTimeout(() => {
        clearErrors(name);
      }, 1000);

      return; // Block input
    }

    // Clear error if we were showing a maxLength error and now we are safe
    if (error?.type === 'maxLength') {
      clearErrors(name);
    }

    onChange(val);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <div className={`form-group ${className}`}>
      {label && (
        <label className={TextCss.black.sub_heading_600_14}>
          {label} {required && <span className="text-danger">*</span>}
        </label>
      )}
      <input
        type="email"
        disabled={disabled}
        className={`form-control ${showErrorMessage ? "is-invalid" : ""}`}
        placeholder={placeholder}
        value={value ?? ""}
        onChange={handleInputChange}
        {...fieldRest}
        {...rest}
      />
      {showErrorMessage && (
        <div className="invalid-feedback">{showErrorMessage as string}</div>
      )}
    </div>
  );
};

export default EmailInput;
